import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createCustomPalette } from '../services/customPalettes';
import tailwindColors from '../data/tailwindColors.json';

const CustomPalettes = ({ onClose, onSave }) => {
  const [paletteName, setPaletteName] = useState('');
  const [theme, setTheme] = useState('light');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Selected colors
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [successColor, setSuccessColor] = useState('');
  const [errorColor, setErrorColor] = useState('');
  
  // Remove theme-dependent reset
  useEffect(() => {
    // No longer resetting colors when theme changes
  }, [theme]);
  
  // Get all colors for selection
  const allColors = tailwindColors;
  
  // Find hex values for selected colors
  const getHexValue = (colorName) => {
    const color = tailwindColors.find(c => c.name === colorName);
    return color ? color.hex : null;
  };
  
  const primaryHex = getHexValue(primaryColor);
  const secondaryHex = getHexValue(secondaryColor);
  const accentHex = getHexValue(accentColor);
  const successHex = getHexValue(successColor);
  const errorHex = getHexValue(errorColor);

  const handleSavePalette = async () => {
    // Validate inputs
    if (!paletteName.trim()) {
      setError('Please enter a palette name');
      return;
    }
    
    if (!primaryColor || !secondaryColor || !accentColor || !successColor || !errorColor) {
      setError('Please select all color options');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      // Create palette object
      const palette = {
        primary: primaryHex,
        secondary: secondaryHex,
        accent: accentHex,
        success: successHex,
        error: errorHex
      };
      
      // Create tailwind palette object (used for class names)
      const tailwindPalette = {
        primary: primaryColor,
        secondary: secondaryColor,
        accent: accentColor,
        success: successColor,
        error: errorColor
      };
      
      // Save the custom palette
      const savedPalette = await createCustomPalette({
        name: paletteName,
        theme,
        palette,
        tailwindPalette
      });
      
      // Notify parent component
      onSave(savedPalette);
    } catch (err) {
      console.error('Error saving custom palette:', err);
      setError('Failed to save palette. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Custom select component with color squares
  const ColorSelect = ({ label, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = React.useRef(null);
    
    // Get appropriate background and border colors based on theme
    const isDarkTheme = theme === 'dark';
    const dropdownBgColor = isDarkTheme ? 'bg-gray-800' : 'bg-white';
    const dropdownBorderColor = isDarkTheme ? 'border-gray-600' : 'border-gray-300';
    const textColor = isDarkTheme ? 'text-white' : 'text-gray-700';
    const placeholderColor = isDarkTheme ? 'text-gray-400' : 'text-gray-500';
    const hoverBgColor = isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
    const selectedBgColor = isDarkTheme ? 'bg-gray-700' : 'bg-gray-100';

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const selectedColor = allColors.find(c => c.name === value);

    return (
      <div className="mb-4">
        <label className={`block text-sm font-medium ${textColor} mb-1`}>
          {label}
        </label>
        
        <div className="relative mt-1" ref={dropdownRef}>
          {/* Custom dropdown trigger */}
          <div
            className={`w-full border ${dropdownBorderColor} rounded-md shadow-sm py-2 px-3 ${dropdownBgColor} cursor-pointer flex items-center justify-between ${textColor}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex items-center">
              {selectedColor ? (
                <>
                  <div 
                    className={`w-4 h-4 rounded mr-2 border ${isDarkTheme ? 'border-gray-600' : 'border-gray-300'}`}
                    style={{ backgroundColor: selectedColor.hex }}
                  ></div>
                  <span>{selectedColor.name}</span>
                </>
              ) : (
                <span className={placeholderColor}>{placeholder || "Select a color"}</span>
              )}
            </div>
            <svg className={`h-5 w-5 ${isDarkTheme ? 'text-gray-400' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
          
          {/* Dropdown menu */}
          {isOpen && (
            <div className={`absolute z-10 mt-1 w-full ${dropdownBgColor} shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm border ${dropdownBorderColor}`}>
              {allColors.map((color) => (
                <div
                  key={color.name}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${hoverBgColor} ${
                    value === color.name ? selectedBgColor : ''
                  } ${textColor}`}
                  onClick={() => {
                    onChange(color.name);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center">
                    <div 
                      className={`w-4 h-4 rounded mr-2 border ${isDarkTheme ? 'border-gray-600' : 'border-gray-300'}`}
                      style={{ backgroundColor: color.hex }}
                    ></div>
                    <span className={value === color.name ? 'font-medium' : 'font-normal'}>
                      {color.name}
                    </span>
                  </div>
                  
                  {value === color.name && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-500">
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`sticky top-0 ${theme === 'dark' ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-black border-gray-300'} p-4 border-b flex justify-between items-center`}>
          <h2 className="text-2xl font-bold">Create Custom Palette</h2>
          <button 
            onClick={onClose} 
            className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
          >
            <X className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
        </div>
        
        {/* Content */}
        <div className={`p-6 overflow-y-auto flex-1 ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
          {error && (
            <div className={`mb-4 p-3 ${theme === 'dark' ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-700'} rounded-md`}>
              {error}
            </div>
          )}
          
          {/* Palette Name */}
          <div className="mb-4">
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-700'} mb-1`}>
              Palette Name
            </label>
            <input
              type="text"
              value={paletteName}
              onChange={e => setPaletteName(e.target.value)}
              className={`mt-1 block w-full border ${theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
              placeholder="My Custom Palette"
            />
          </div>
          
          {/* Theme Selection */}
          <div className="mb-6">
            <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-700'} mb-1`}>
              Theme
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setTheme('light')}
                className={`px-4 py-2 rounded-md ${
                  theme === 'light' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                Light
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={`px-4 py-2 rounded-md ${
                  theme === 'dark' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                Dark
              </button>
            </div>
          </div>
          
          {/* Color Selections */}
          <div className="space-y-4">
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Select Colors</h3>
            
            <ColorSelect 
              label="Primary Color"
              value={primaryColor}
              onChange={setPrimaryColor}
              placeholder="Select primary color"
            />
            
            <ColorSelect 
              label="Secondary Color"
              value={secondaryColor}
              onChange={setSecondaryColor}
              placeholder="Select secondary color"
            />
            
            <ColorSelect 
              label="Accent Color"
              value={accentColor}
              onChange={setAccentColor}
              placeholder="Select accent color"
            />
            
            <ColorSelect 
              label="Success Color"
              value={successColor}
              onChange={setSuccessColor}
              placeholder="Select success color"
            />
            
            <ColorSelect 
              label="Error Color"
              value={errorColor}
              onChange={setErrorColor}
              placeholder="Select error color"
            />
          </div>
          
          {/* Preview */}
          {primaryHex && secondaryHex && accentHex && (
            <div className={`mt-6 border ${theme === 'dark' ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} p-4 rounded-md`}>
              <h3 className="text-lg font-medium mb-2">Preview</h3>
              <div className="flex space-x-2">
                <div 
                  className={`w-12 h-12 rounded border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                  style={{ backgroundColor: primaryHex }}
                ></div>
                <div 
                  className={`w-12 h-12 rounded border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                  style={{ backgroundColor: secondaryHex }}
                ></div>
                <div 
                  className={`w-12 h-12 rounded border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                  style={{ backgroundColor: accentHex }}
                ></div>
                <div 
                  className={`w-12 h-12 rounded border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                  style={{ backgroundColor: successHex }}
                ></div>
                <div 
                  className={`w-12 h-12 rounded border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                  style={{ backgroundColor: errorHex }}
                ></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`sticky bottom-0 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} p-4 border-t flex justify-end`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md ${
              theme === 'dark' ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            } mr-2`}
          >
            Cancel
          </button>
          <button
            onClick={handleSavePalette}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-black text-white"
          >
            {saving ? 'Saving...' : 'Save Palette'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomPalettes;